import { Button, Stack, Typography, useTheme } from '@mui/material'
import { Box } from '@mui/system'
import { Accept, DropEvent, FileRejection, useDropzone } from 'react-dropzone'
import { useTranslation } from 'react-i18next'
import LoadingState from './LoadingState'
import { ReactNode } from 'react'

export interface DropzoneProps {
  icon?: ReactNode
  label?: string
  buttonText?: string
  accept?: Accept
  capture?: 'user' | 'environment'
  uploading?: boolean
  multiple?: boolean
  disabled?: boolean
  minHeight?: number | string
  height?: number | string
  dragText?: string
  activeDragText?: string
  selectedFileName?: string
  onDrop?: <T extends File>(
    acceptedFiles: T[],
    fileRejections: FileRejection[],
    event: DropEvent
  ) => void
}

export default function Dropzone({
  icon,
  label,
  buttonText,
  accept,
  capture,
  uploading = false,
  multiple = false,
  disabled = false,
  minHeight = 200,
  height = '33vh',
  dragText,
  activeDragText,
  selectedFileName,
  onDrop
}: DropzoneProps) {
  /**
   * The translation function.
   */
  const { t } = useTranslation()

  /**
   * The theme.
   */
  const theme = useTheme()

  /**
   * The dropzone state.
   */
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept,
    multiple,
    disabled: uploading || disabled
  })

  return (
    <Box
      minHeight={minHeight}
      height={height}
      width="100%"
      display="flex"
      alignItems="center"
      justifyContent="center"
      sx={{
        backgroundColor: theme.palette.background.default,
        borderRadius: 1,
        borderColor: theme.palette.divider,
        borderStyle: 'dashed',
        borderWidth: 2,
        width: '100%',
        cursor: 'pointer'
      }}
      {...getRootProps()}
    >
      <Stack direction="column" justifyContent="center" alignItems="center" gap={4} width="100%">
        <input disabled={uploading} {...getInputProps({ capture })} />

        {uploading ? (
          <LoadingState />
        ) : (
          <>
            {icon}
            {label && <Typography variant="h6">{label}</Typography>}

            <Stack direction="row" justifyContent="center" alignItems="center" gap={2}>
              <Button component="label" variant="contained" color="primary" disabled={disabled}>
                {buttonText ?? t('selectFileFromYourDevice')}
              </Button>
              <Typography>
                {selectedFileName
                  ? selectedFileName
                  : isDragActive
                    ? (activeDragText ?? t('dropHere'))
                    : (dragText ?? t('orDragHere'))}
              </Typography>
            </Stack>
          </>
        )}
      </Stack>
    </Box>
  )
}
